import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/lib/company";
import { fmtDate } from "@/lib/money";
import { Page, Btn, Field, Loading } from "@/components/kit";

const input = "min-h-[40px] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800 disabled:bg-slate-50";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function Setup() {
  const { company, reload } = useCompany();
  const { user } = useAuth();
  if (!company) return <Page title="Company setup"><Loading /></Page>;
  return <SetupForm key={company.id} company={company} reload={reload} canEdit={user?.role === "admin"} />;
}

function SetupForm({ company, reload, canEdit }) {
  const [form, setForm] = useState({ name: company.name, kra_pin: company.kra_pin || "", fy_start_month: company.fy_start_month || 1,
    books_start: company.books_start, vat_registered: !!company.vat_registered, address: company.address || "" });
  const save = async () => {
    try {
      await api.put(`/companies/${company.id}`, { ...form, fy_start_month: Number(form.fy_start_month) });
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const f = (k) => ({ value: form[k], disabled: !canEdit, onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <Page title="Company setup">
      <div className="mt-5 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", alignItems: "start" }}>
        <section className="rounded-[10px] border border-[#E4E2DE] bg-white p-5">
          <h2 className="mb-4 text-[15px] font-semibold">Company</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company name"><input className={input} {...f("name")} /></Field>
            <Field label="KRA PIN"><input className={input} {...f("kra_pin")} /></Field>
            <Field label="Books start"><input type="date" className={input} {...f("books_start")} /></Field>
            <Field label="Financial year starts">
              <select className={input} {...f("fy_start_month")}>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
            </Field>
            <Field label="Address"><input className={input} {...f("address")} /></Field>
            <label className="flex items-end gap-2 pb-2 text-sm"><input type="checkbox" checked={form.vat_registered} disabled={!canEdit} onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })} /> VAT registered</label>
          </div>
          {canEdit && <Btn kind="primary" className="mt-4" onClick={save}>Save</Btn>}
        </section>
        <section className="rounded-[10px] border border-[#E4E2DE] bg-white p-5">
          {(company.stations || []).length > 0 && <h2 className="mb-2 text-[15px] font-semibold">Branches</h2>}
          {(company.stations || []).map((s) => (
            <div key={s} className="flex justify-between border-b border-[#EEECE8] py-3 text-sm"><span className="font-semibold">{s}</span><span /></div>
          ))}
          <h2 className="mb-2 text-[15px] font-semibold">Opening balances · {fmtDate(company.books_start)}</h2>
          <p className="text-sm text-[#5F5C58]">Money and assets the company started with, and what it owed.</p>
          <Link to="/journals/new?kind=opening" className="mt-3 inline-flex min-h-[40px] items-center rounded-md border border-slate-300 px-4 text-sm font-semibold hover:bg-slate-50">Enter opening balances</Link>
        </section>
      </div>
    </Page>
  );
}
